import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { useUser } from 'src/entities/user/hooks/useUser';
import { useAuthStore } from 'src/entities/user/store/useAuthStore';
import { ROUTE_PATHS } from 'src/shared/config/routes';

export const AuthProviderV2 = ({ children }: { children: React.ReactNode }) => {
  const authToken = useAuthStore((state) => state.authToken);
  const setAuthToken = useAuthStore((state) => state.setAuthToken);
  const fallBackToken = useAuthStore((state) => state.fallBackToken);
  const setFallBackToken = useAuthStore((state) => state.setFallBackToken);
  const setSelectedOrganization = useAuthStore(
    (state) => state.setSelectedOrganization
  );
  const setImpersonation = useAuthStore((state) => state.setImpersonation);
  const newToken = useAuthStore((state) => state.newToken);
  const setNewToken = useAuthStore((state) => state.setNewToken);

  const [shouldRefetch, setShouldRefetch] = useState(false);

  const { isError, refetch: refetchUser, error, data } = useUser();

  const queryClient = useQueryClient();

  const searchParams = new URLSearchParams(window.location.search);
  const impersonationQuery = searchParams.get('impersonation');

  // refetch user and queries
  useEffect(() => {
    if (!shouldRefetch) return;

    refetchUser();

    // fixes incorrect organization ID when we change user using auth_token in PrivateRoute
    setTimeout(() => {
      queryClient.invalidateQueries();
    }, 300);

    // Re-arm the trigger. shouldRefetch is a one-shot signal, not a persistent
    // state: leaving it true means a later setShouldRefetch(true) is a no-op
    // (same value) and this effect never re-runs. The invalid-token fallback
    // relies on that re-run to re-validate /me against the fallback token — if
    // an earlier branch (impersonation, not-authorized) already set it true,
    // the fallback would swap the token but never refetch, leaving the session
    // stuck with a stale error and no redirect.
    setShouldRefetch(false);
  }, [shouldRefetch]);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const queryAuthToken = searchParams.get('auth_token');

    if (queryAuthToken) {
      if (!fallBackToken) {
        setFallBackToken(queryAuthToken);
      }

      if (!authToken || authToken !== queryAuthToken) {
        setSelectedOrganization(null);
        setAuthToken(queryAuthToken);
        setNewToken(queryAuthToken);
        // Force /me to be re-validated against the new token so an invalid URL
        // token surfaces as an error and the fallback branch can restore
        // fallBackToken. Without this, the cached /me response from the
        // previous (good) token keeps isError=false and the bad token silently
        // replaces the stored one.
        queryClient.invalidateQueries({ queryKey: ['user'] });
      }

      // Clean up auth_token from URL

      const url = new URL(window.location.href);

      url.searchParams.delete('auth_token');
      window.history.replaceState(null, '', url);
    }

    if (
      isError &&
      error.message === 'You are not authorized to access this organization'
    ) {
      setSelectedOrganization(null);
      setAuthToken(newToken);
      setShouldRefetch(true);
    }
  }, [
    authToken,
    fallBackToken,
    isError,
    error,
    setAuthToken,
    setFallBackToken,
    setSelectedOrganization,
    newToken,
    setNewToken
  ]);

  // handle invalid token: fall back to the last known-good token once, then
  // give up and treat the session as expired.
  //
  // Deliberately keyed on [isError, error] only, NOT authToken/fallBackToken.
  // Falling back sets authToken to fallBackToken, which would itself
  // re-trigger this effect on the very next render if those were deps — but
  // isError/error are still stale at that point (they describe the *previous*
  // token's failure, since the fallback request hasn't resolved yet), so the
  // effect would redirect to /expired before the fallback attempt ever ran.
  // Reading the store directly via getState() (rather than the reactive
  // authToken/fallBackToken above) guarantees we see whatever the fallback
  // branch below just wrote, without needing those as deps.
  useEffect(() => {
    if (!isError || error.message !== 'Invalid token') return;

    const { authToken: currentAuthToken, fallBackToken: currentFallBackToken } =
      useAuthStore.getState();

    if (currentAuthToken === currentFallBackToken) {
      setNewToken(null);
      setAuthToken(null);
      setFallBackToken(null);
      window.location.href = ROUTE_PATHS.EXPIRED;
    } else {
      setAuthToken(currentFallBackToken);
      setShouldRefetch(true);
    }
  }, [isError, error, setAuthToken, setFallBackToken, setNewToken]);

  // impersonation control
  useEffect(() => {
    if (impersonationQuery) {
      if (data?.isStaff || data?.impersonating) {
        // when impersonation query is 0, stop impersonation
        if (impersonationQuery === '0') {
          setImpersonation(null);
          setSelectedOrganization(null);
          setShouldRefetch(true);
        }

        // when impersonation query is not 0, start impersonation
        if (impersonationQuery && impersonationQuery !== '0') {
          setImpersonation(impersonationQuery);
          setSelectedOrganization(null);
          setShouldRefetch(true);

          const url = new URL(window.location.href);

          url.searchParams.delete('impersonation');
          window.history.replaceState(null, '', url);
        }
      }

      if (error?.message === 'Only staff can impersonate') {
        setImpersonation(null);
        setSelectedOrganization(null);
        setShouldRefetch(true);

        window.location.href = '/products';
      }
    }
  }, [data?.isStaff, error?.message, data?.impersonating, impersonationQuery]);

  // handle organization url
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const organizationId = searchParams.get('organization');

    if (organizationId && data) {
      const selectedOrganization =
        data?.organizations?.find(
          (org) => org.id.toString() === organizationId
        ) ?? null;

      if (selectedOrganization) {
        setSelectedOrganization(selectedOrganization);
      }

      const url = new URL(window.location.href);

      url.searchParams.delete('organization');
      window.history.replaceState(null, '', url);
    }
  }, [data]);

  if (impersonationQuery) {
    // only render app if no impersonation query
    return null;
  }

  return children;
};

export default AuthProviderV2;
